import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getAuth } from 'firebase/auth';
import { 
  HelpCircle, 
  Send, 
  CheckCircle, 
  ChevronDown, 
  ChevronUp,
  BookOpen,
  MessageSquare,
  Brain,
  Target,
  Award,
  Lock,
  Smartphone,
  Wifi,
  Bell,
  Shield
} from 'lucide-react';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  icon: React.ElementType;
}

const faqs: FAQ[] = [
  // Getting Started
  {
    id: 'what-is-ggg',
    question: 'What is Grit, Growth & Gratitude?',
    answer: 'Grit, Growth & Gratitude (GGG) is a mental wellness platform designed specifically for first responders. It combines evidence-based training modules, guided meditations, daily journaling, and peer support to help you build resilience and maintain mental fitness.',
    category: 'Getting Started',
    icon: BookOpen
  },
  {
    id: 'how-to-start',
    question: 'How do I get started?',
    answer: 'Start with the Dashboard! Complete your first Morning Check-In to set intentions for your day, then explore Training Videos to learn evidence-based wellness techniques. We recommend starting with the foundation modules on stress management and resilience.',
    category: 'Getting Started',
    icon: Target
  },
  {
    id: 'access-code',
    question: 'Do I need an access code?',
    answer: 'You can use the Journal, G.R.I.P.S. chatbot, and Community features without an access code. However, Training Videos, Meditations, and Achievements require an access code provided by your department or organization.',
    category: 'Getting Started',
    icon: Lock
  },
  // Journal & Check-Ins
  {
    id: 'journal-purpose',
    question: 'What are Morning and Evening Check-Ins?',
    answer: 'Morning Check-Ins help you start your day with gratitude, set mini goals, and establish your daily intention. Evening Check-Ins let you reflect on wins, review your progress, and end your day positively. Both include mood tracking to monitor your wellness over time.',
    category: 'Journal & Check-Ins',
    icon: MessageSquare
  },
  {
    id: 'journal-privacy',
    question: 'Are my journal entries private?',
    answer: 'Yes! Your journal entries are completely private and stored securely. Only you can see them unless you explicitly choose to share specific insights with your department or therapist (features available in settings).',
    category: 'Journal & Check-Ins',
    icon: Shield
  },
  {
    id: 'missed-checkin',
    question: 'What if I miss a check-in?',
    answer: 'No worries! You can complete check-ins anytime. While consistency helps build habits, missing a day won\'t reset your progress. You can view your history in the calendar to see your journey over time.',
    category: 'Journal & Check-Ins',
    icon: MessageSquare
  },
  // Training & Meditations
  {
    id: 'training-modules',
    question: 'How do the training modules work?',
    answer: 'Training modules are self-paced courses covering topics like stress management, sleep optimization, and resilience building. Each module includes video lessons, exercises, and quizzes. Your progress is automatically saved, and you can return anytime.',
    category: 'Training & Meditations',
    icon: BookOpen
  },
  {
    id: 'meditation-guide',
    question: 'I\'ve never meditated before. Where should I start?',
    answer: 'Start with our "Introduction to Mindfulness" meditation (Day 1). It\'s only 5 minutes and guides you through the basics. The meditations are organized in a progressive order, so just follow Day 1, Day 2, etc.',
    category: 'Training & Meditations',
    icon: Brain
  },
  {
    id: 'module-completion',
    question: 'How do I track my progress?',
    answer: 'Your progress is automatically tracked! Check the Achievements page to see completed modules, earned badges, and your wellness journey. Completed training modules show a green checkmark on the Training Hub.',
    category: 'Training & Meditations',
    icon: Award
  },
  // G.R.I.P.S.
  {
    id: 'grips-what',
    question: 'What is G.R.I.P.S.?',
    answer: 'G.R.I.P.S. (Grit & Resilience Integrated Peer Support) is your 24/7 AI wellness coach. It provides immediate support, coping strategies, and resources based on your specific situation. Think of it as always-available peer support.',
    category: 'G.R.I.P.S.',
    icon: MessageSquare
  },
  {
    id: 'grips-privacy',
    question: 'Is my conversation with G.R.I.P.S. private?',
    answer: 'Yes! Your G.R.I.P.S. conversations are private and stored locally on your device. They are not shared with your department or stored on our servers.',
    category: 'G.R.I.P.S.',
    icon: Shield
  },
  // Technical Issues
  {
    id: 'offline-mode',
    question: 'Can I use the app offline?',
    answer: 'The app works best with an internet connection, but you can access previously loaded content offline. Your check-ins and progress will sync automatically when you reconnect.',
    category: 'Technical Issues',
    icon: Wifi
  },
  {
    id: 'mobile-install',
    question: 'How do I install the app on my phone?',
    answer: 'On iPhone: Open in Safari, tap the Share button, then "Add to Home Screen". On Android: Open in Chrome, tap the menu (three dots), then "Add to Home Screen" or "Install App".',
    category: 'Technical Issues',
    icon: Smartphone
  },
  {
    id: 'notifications',
    question: 'How do I enable check-in reminders?',
    answer: 'Go to your Profile settings and enable notifications. You can set custom times for morning and evening check-in reminders. Make sure to allow notifications in your browser or device settings.',
    category: 'Technical Issues',
    icon: Bell
  },
  {
    id: 'data-sync',
    question: 'My progress isn\'t showing up. What should I do?',
    answer: 'First, check your internet connection. If connected, try refreshing the page. If the issue persists, sign out and sign back in. Your data is securely stored and will sync once reconnected.',
    category: 'Technical Issues',
    icon: Wifi
  }
];

const categories = [
  'Getting Started',
  'Journal & Check-Ins',
  'Training & Meditations',
  'G.R.I.P.S.',
  'Technical Issues'
];

export const Support: React.FC = () => {
  const { user } = useAuth();
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('Getting Started');
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    description: '',
    email: user?.email || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Check if scrolling is possible
  useEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowScrollIndicator(scrollWidth > clientWidth);
      }
    };

    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    
    // Also check after a short delay to account for layout
    const timeoutId = setTimeout(checkScrollable, 100);

    return () => {
      window.removeEventListener('resize', checkScrollable);
      clearTimeout(timeoutId);
    };
  }, []);

  // Update scroll indicator when scrolling
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      // Show left fade if scrolled right
      setShowLeftFade(scrollLeft > 10);
      // Hide right indicator if scrolled all the way to the right
      setShowScrollIndicator(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Save to Firestore
      await addDoc(collection(db, 'supportTickets'), {
        ...formData,
        userId: user?.uid,
        userName: user?.displayName,
        status: 'open',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Send email via Firebase Function
      try {
        const auth = getAuth();
        const firebaseUser = auth.currentUser;

        if (!firebaseUser) {
          console.warn('⚠️ No Firebase auth user found – skipping email send');
        } else {
          // Get the Firebase ID token for authentication
          const idToken = await firebaseUser.getIdToken();

          const response = await fetch('https://sendsupportemail-hcumtjufca-uc.a.run.app', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              email: formData.email,
              category: formData.category,
              subject: formData.subject,
              description: formData.description,
              userName: user?.displayName || 'Unknown User'
            })
          });

          const result = await response.json();
          console.log('✅ Support email sent successfully:', result);
        }
      } catch (emailError) {
        console.error('⚠️ Email sending failed, but ticket was saved:', emailError);
        // Continue anyway - ticket is still saved to Firestore
      }

      setSubmitted(true);
      setFormData({
        subject: '',
        category: '',
        description: '',
        email: user?.email || ''
      });

      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredFaqs = faqs.filter(faq => faq.category === selectedCategory);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-6 md:p-8 text-white">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-white bg-opacity-20 p-3 rounded-xl">
            <HelpCircle className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Tech Support</h1>
            <p className="text-blue-100 text-sm md:text-base">We're here to help you get the most out of GGG</p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a
          href="#faq"
          className="bg-white rounded-xl p-4 border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all duration-200 group"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">FAQs</h3>
              <p className="text-xs text-gray-600">Common questions answered</p>
            </div>
          </div>
        </a>
        <a
          href="#troubleshooting"
          className="bg-white rounded-xl p-4 border border-gray-200 hover:border-purple-500 hover:shadow-md transition-all duration-200 group"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg group-hover:bg-purple-200 transition-colors">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Troubleshooting</h3>
              <p className="text-xs text-gray-600">Fix common issues</p>
            </div>
          </div>
        </a>
        <a
          href="#contact"
          className="bg-white rounded-xl p-4 border border-gray-200 hover:border-green-500 hover:shadow-md transition-all duration-200 group"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg group-hover:bg-green-200 transition-colors">
              <MessageSquare className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Contact Us</h3>
              <p className="text-xs text-gray-600">Submit a support request</p>
            </div>
          </div>
        </a>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
        
        {/* Category Tabs - Horizontal Scroll with Visual Indicators */}
        <div className="relative mb-6">
          {/* Scrollable Container */}
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto gap-3 pb-4 scrollbar-hide scroll-smooth"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 transparent',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2.5 rounded-lg font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 text-sm md:text-base ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          
          {/* Fade Gradient on Right Side - Scroll Indicator (only show if scrollable) */}
          {showScrollIndicator && (
            <div 
              className="absolute top-0 right-0 bottom-4 w-16 pointer-events-none bg-gradient-to-l from-white via-white to-transparent transition-opacity duration-300"
              style={{
                animation: 'pulse-fade 2s ease-in-out infinite'
              }}
            />
          )}
          
          {/* Left side fade (when scrolled) */}
          {showLeftFade && (
            <div 
              className="absolute top-0 left-0 bottom-4 w-16 pointer-events-none bg-gradient-to-r from-white via-white to-transparent transition-opacity duration-300"
            />
          )}
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {filteredFaqs.map(faq => {
            const Icon = faq.icon;
            const isExpanded = expandedFaq === faq.id;
            
            return (
              <div
                key={faq.id}
                className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-all duration-200"
              >
                <button
                  onClick={() => setExpandedFaq(isExpanded ? null : faq.id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                      <Icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">{faq.question}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                  )}
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Troubleshooting Section */}
      <div id="troubleshooting" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Quick Troubleshooting</h2>
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
              <Wifi className="w-5 h-5 mr-2" />
              App won't load or sync
            </h3>
            <ul className="text-sm text-purple-800 space-y-1 ml-7">
              <li>• Check your internet connection</li>
              <li>• Refresh the page (pull down on mobile)</li>
              <li>• Clear your browser cache and reload</li>
              <li>• Try signing out and back in</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
              <Smartphone className="w-5 h-5 mr-2" />
              App icon disappeared from home screen
            </h3>
            <ul className="text-sm text-blue-800 space-y-1 ml-7">
              <li>• Reinstall by visiting the website and adding to home screen again</li>
              <li>• Your data is saved in the cloud and will sync automatically</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <h3 className="font-semibold text-green-900 mb-2 flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Can't access training or meditations
            </h3>
            <ul className="text-sm text-green-800 space-y-1 ml-7">
              <li>• Ensure you've entered a valid access code in your profile</li>
              <li>• Contact your department administrator for an access code</li>
              <li>• You can still use Journal, G.R.I.P.S., and Community without a code</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h3 className="font-semibold text-amber-900 mb-2 flex items-center">
              <Bell className="w-5 h-5 mr-2" />
              Not receiving check-in reminders
            </h3>
            <ul className="text-sm text-amber-800 space-y-1 ml-7">
              <li>• Check notification settings in your Profile</li>
              <li>• Ensure notifications are enabled in your device/browser settings</li>
              <li>• For iPhone: Settings → Safari → Advanced → Notifications</li>
              <li>• For Android: Settings → Apps → Chrome → Notifications</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div id="contact" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Still Need Help?</h2>
        <p className="text-gray-600 mb-6">Submit a support request and we'll get back to you within 24-48 hours.</p>

        {submitted ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">Request Submitted!</h3>
            <p className="text-green-700">We've received your support request and will respond via email within 24-48 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category
              </label>
              <select
                name="category"
                required
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a category</option>
                <option value="technical">Technical Issue</option>
                <option value="account">Account & Access</option>
                <option value="content">Training/Meditation Content</option>
                <option value="feature">Feature Request</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                name="subject"
                required
                value={formData.subject}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief description of your issue"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                required
                value={formData.description}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Please provide as much detail as possible about your issue or question..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <Send className="w-5 h-5" />
              <span>{submitting ? 'Submitting...' : 'Submit Request'}</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

